/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Pressable, TextField, Icon, ErrorText } from '@components/atoms'

import * as locale from 'locale-codes'
import { useAppSelector } from '@redux/index'
import { deepPurple, grey } from '@mui/material/colors'
import { I18nType } from '@schemas'

interface PropsType {
	title?: string
	className?: string
	i18n: I18nType[]
	tableName: string
	tableId: string
	menuId: string
	value: string
	disabled?: boolean
	onSave?: (newI18n: I18nType) => void
}

export const I18nEditor = (props: PropsType) => {
	// redux state
	const i18nLanguageList: string[] = useAppSelector(
		state => state.i18n.supportedLanguageList,
	)

	const emptyI18n: I18nType = {
		id: '',
		tableName: '',
		tableId: '',
		menuId: '',
		isoLanguageCode: '',
		value: '',
		title: '',
	}

	// state
	const [editI18n, setEditI18n] = React.useState<I18nType>(emptyI18n)
	const [showError, setShowError] = React.useState<boolean>(false)

	// varialbes
	const disabled: boolean = props.disabled ?? false

	const onEditClick = React.useCallback((anI18n: I18nType) => {
		setEditI18n(anI18n)
		setShowError(false)
	}, [])

	const onTextChange = (e: any) => {
		const newTitle = e.target.value

		const newEditI18n: I18nType = { ...editI18n, title: newTitle }
		setEditI18n(newEditI18n)
	}

	const onInputEnterSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			onSaveClick()
		}
	}

	const onSaveClick = React.useCallback(() => {
		if (editI18n.title === '') {
			setShowError(true)
		} else {
			const newI18n: I18nType = {
				id: editI18n.id,
				tableName: props.tableName,
				tableId: props.tableId,
				menuId: props.menuId,
				isoLanguageCode: editI18n.isoLanguageCode,
				value: '',
				title: editI18n.title,
			}

			setEditI18n(emptyI18n)

			if (props.onSave) {
				props.onSave(newI18n)
			}
		}
	}, [editI18n])

	return (
		<View css={style} className={props.className ?? ''}>
			<View className="i18nView">
				{props.title && (
					<Text className="title" category="c1">
						{props.title}
					</Text>
				)}
				<View className="i18nListView">
					{i18nLanguageList.map(anLanguageCode => {
						const localeData: locale.ILocale = locale.getByTag(anLanguageCode)
						const thisI18n = props.i18n.filter(
							anI18n =>
								anI18n.isoLanguageCode.toLowerCase() === anLanguageCode.toLowerCase(),
						)

						const code = localeData.tag
						const languageName: string =
							code === 'zh-CN'
								? '中文'
								: localeData.local
								? localeData.local
								: localeData.name
								? localeData.name
								: ''
						const title = thisI18n.length > 0 ? thisI18n[0].title : '-'
						const isEditMode: boolean = editI18n.isoLanguageCode === code

						return (
							<View key={anLanguageCode} className="i18nItemOuter">
								<View className={`i18nItemView ${disabled ? 'disabled' : ''}`}>
									<View className="i18nItemTextView">
										<Text
											className="i18nItemLabel"
											category="c2"
										>{`${languageName} (${code})`}</Text>
										{isEditMode ? (
											<>
												<TextField
													className="newI18nTextField"
													size="small"
													autoFocus={true}
													defaultValue={title === '-' ? '' : title}
													onChange={onTextChange}
													error={showError}
													onKeyUp={onInputEnterSubmit}
												/>
												{showError && (
													<ErrorText category="c2" css={{ marginBottom: 0 }}>
														빈 텍스트는 등록할 수 없습니다.
													</ErrorText>
												)}
											</>
										) : (
											<Text className="i18nTitle" category="s1">
												{title}
											</Text>
										)}
									</View>

									{isEditMode ? (
										<Pressable
											className="i18nItemRightButton save"
											onClick={onSaveClick}
											disabled={disabled}
										>
											<Icon name="check-bold" className="i18nItemRightIcon" />
										</Pressable>
									) : (
										<>
											{!disabled && (
												<Pressable
													className="i18nItemRightButton"
													onClick={() =>
														onEditClick(
															thisI18n.length > 0
																? {
																		...thisI18n[0],
																		isoLanguageCode: code,
																		title: title,
																  }
																: {
																		id: 'new',
																		tableName: props.tableName,
																		tableId: props.tableId,
																		menuId: props.menuId,
																		isoLanguageCode: code,
																		value: '',
																		title: '',
																  },
														)
													}
													disabled={disabled}
												>
													<Icon name="pencil" className="i18nItemRightIcon" />
												</Pressable>
											)}
										</>
									)}
								</View>
							</View>
						)
					})}
				</View>
			</View>
		</View>
	)
}

const style = css({
	width: '100%',
	height: '100%',

	'.i18nView': {
		overflow: 'hidden',
		width: '100%',
		height: '100%',
		background: grey[50],
		border: `1px solid ${grey[200]}`,
		borderRadius: '5px',

		'.title': {
			paddingLeft: '0.5rem',
			textAlign: 'left',
		},

		'.i18nListView': {
			height: 'calc(100% - 2.5rem)',
			maxHeight: 'calc(100% - 2.5rem)',
			overflow: 'auto',
			'.i18nItemOuter': {
				padding: '0.1rem 0.2rem',

				'.i18nItemView': {
					position: 'relative',
					width: '100%',
					height: 'fit-content',
					padding: '0.5rem',
					background: '#FFF',
					border: '1px solid',
					borderColor: grey[200],
					borderRadius: '5px',
					textAlign: 'left',

					'&.disabled': {
						opacity: '0.5',
					},

					'.i18nItemTextView': {
						'.i18nItemLabel': {
							margin: '0.2rem 0',
						},
						'.i18nTitle': {
							margin: '1rem 0 0.3rem',
						},

						'.newI18nTextField': {
							width: '10rem',
							top: '0.2rem',
						},
					},

					'.i18nItemRightButton': {
						position: 'absolute',
						width: '1.8rem',
						height: '1.8rem',
						top: 0,
						bottom: 0,
						right: '0.2rem',
						margin: 'auto',
						borderRadius: '50%',
						background: grey[100],

						'&.save': {
							background: deepPurple['A400'],

							'.i18nItemRightIcon': {
								color: '#FFF',
							},
						},

						'.i18nItemRightIcon': {
							color: grey[600],
							fontSize: '16px',
						},
					},
				},
			},
		},
	},
})
